
/* REACT */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

/* UTILS */
import { BrandConfig } from '@/store/brand-config/brand-config.model';
import { useAxios } from '@/api/useTPAxios.ts';
import NotFound from '@/screens/NotFound/NotFound';
import { ClipLoader } from 'react-spinners';
import { fonts, weightMap } from './custom-fonts.utils.ts';
import { SettingsStore } from '@/store/settings/settings.store.ts';
import { overrideTextStyles, Styles } from './custom-styles.ts';
import { loadCustomTranslations } from './i18n.utils.ts';

export const BrandConfigContext = createContext<BrandConfig | undefined>(undefined);

/* CUSTOM HOOK FOR BRAND CONFIG */
export const useBrandConfig = (): BrandConfig => {
  const context = useContext(BrandConfigContext);
  if (!context) {
    throw new Error('useBrandConfig must be used within a BrandConfigProvider');
  }
  return context;
};

interface BrandConfigProviderProps {
  children: ReactNode;
}

interface GetConfigApiResponse {
  data: BrandConfig
}

/* BRAND CONFIG PROVIDER */
export const BrandConfigProvider: React.FC<BrandConfigProviderProps> = ({
  children,
}) => {
  const [brandConfig, setBrandConfig] = useState<BrandConfig | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const TPAxios = useAxios();

  const defaultFavicon = 'https://res.cloudinary.com/dnkj85yan/image/upload/v1690540652/certificate-request-page/brands/brand%20z/favicon.png';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await TPAxios.get<GetConfigApiResponse>(`/brands/public/config/digital-wallet`);

        const config = response.data?.data;
        const { favicon, font } = config.digitalWalletConfig;

        const faviconLink = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (faviconLink) {
          faviconLink.href = favicon || defaultFavicon;
        } else {
          const newFavicon = document.createElement('link');
          newFavicon.rel = 'icon';
          newFavicon.href = favicon || defaultFavicon;
          document.head.appendChild(newFavicon);
        }

        const brandName = config.name.toLowerCase().replace(/\s+/g, '');

        if (brandName === 'andréeputman') {
          document.styleSheets[0].insertRule('.sku { display: none !important; }', 0);
        } else {
          document.styleSheets[0].insertRule('.history-subtitle { display: none !important; }', 0);
        }

        SettingsStore.getState().setBrandName(brandName);
        loadCustomTranslations(brandName);

        const customFont = fonts[brandName];

        if (customFont) {
          const style = document.createElement('style');

          style.textContent = Object.entries(customFont.files)
            .map(([weight, url]) => `
            @font-face {
              font-family: "${customFont.name}";
              src: url("${url}") format("truetype");
              font-weight: ${weightMap[weight.split('_')?.[0]] ?? '400'};
              font-style: ${weight.includes('italic') ? 'italic' : 'normal'};
            }
          `)
            .join('\n');

          document.head.appendChild(style);
          document.body.style.fontFamily = `"${customFont.name}", sans-serif`;
        }
        else if (font) {
          const link = document.createElement('link');
          link.href = font.url;
          link.rel = 'stylesheet';
          document.head.appendChild(link);

          document.body.style.fontFamily = `${font.name}, sans-serif`;
        }

        const customStyles = Styles[brandName];

        if (customStyles) {
          overrideTextStyles(customStyles);
        }

        setBrandConfig(config);
      } catch (error) {
        console.error('Failed to fetch brand config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  if (loading) return (
    <div className="flex h-[100vh] justify-center items-center">
      <ClipLoader />
    </div>
  );

  if (!brandConfig) return <NotFound />;

  return (
    <BrandConfigContext.Provider value={brandConfig} >
      {children}
    </BrandConfigContext.Provider>
  );
};
